import { Input } from '@acko-tech/building-blocks.ui.input';
import { Dropdown } from '@acko-tech/building-blocks.ui.dropdown';
import { PrimaryButton } from '@acko-tech/building-blocks.ui.button';
import Checkbox from 'library/checkbox';
import DocumentUploadWidget from 'widgets/documentUpload';
import Image from 'next/image';
import * as Styles from './styles';
import {
    FinancialFormValues,
    IFinancialFieldConfig,
    SECTION_TITLES,
} from './types';
import {
    groupFieldsBySection,
    isFieldEditable,
    isFieldVisible,
    mapDropdownOptions,
} from './helpers';

interface FinancialDetailsFormProps {
    mode: 'capture' | 'confirm';
    description: string;
    buttonName: string;
    uiConfig: IFinancialFieldConfig[];
    values: FinancialFormValues;
    validationErr: Record<string, string>;
    isBtnLoading: boolean;
    isBtnDisabled: boolean;
    isFetchingIfsc: boolean;
    errorMessage?: string;
    ctaPressed?: boolean;
    onChangeValue: (key: string, value: any) => void;
    onBlurField: (field: IFinancialFieldConfig) => void;
    onSelectFile: (
        id: string,
        file: File,
        field: IFinancialFieldConfig
    ) => void;
    onDeleteFile: (field: IFinancialFieldConfig) => void;
    onClickTncLink: () => void;
    onSubmit: () => void;
}

function FinancialDetailsForm({
    mode,
    description,
    buttonName,
    uiConfig,
    values,
    validationErr,
    isBtnLoading,
    isBtnDisabled,
    isFetchingIfsc,
    errorMessage,
    ctaPressed,
    onChangeValue,
    onBlurField,
    onSelectFile,
    onDeleteFile,
    onClickTncLink,
    onSubmit,
}: FinancialDetailsFormProps) {
    const sections = groupFieldsBySection(uiConfig, values);
    const tncField = uiConfig.find(
        (field) => field.component === 'tnc_checkbox'
    );
    const gstExpanded = Boolean(values.gst_applicable);

    const renderCaptureField = (field: IFinancialFieldConfig) => {
        const { key_name: key, transform } = field.props;
        const value = values[key];
        const err = validationErr[key] || '';
        const editable = isFieldEditable(field);

        if (field.component === 'input') {
            return (
                <Styles.ItemWrapper
                    key={field.id}
                    onBlur={() => onBlurField(field)}
                >
                    <Input
                        name={key}
                        type={field.type || 'text'}
                        className={''}
                        value={value || ''}
                        onChange={(e) => {
                            if (!editable) return;
                            let next = e.target.value;
                            if (transform === 'uppercase') {
                                next = next.toUpperCase();
                            }
                            onChangeValue(key, next);
                        }}
                        placeHolder={field.label || field.title || ''}
                        hasError={err.length > 0}
                        errMsg={err}
                        disabled={!editable}
                        rightElement={
                            key === 'ifsc_code' && isFetchingIfsc ? (
                                <Image
                                    src={'/gif/greenCircleLoader.gif'}
                                    alt={''}
                                    width={20}
                                    height={20}
                                />
                            ) : (
                                <></>
                            )
                        }
                    />
                </Styles.ItemWrapper>
            );
        }

        if (field.component === 'dropdown') {
            return (
                <Styles.ItemWrapper key={field.id}>
                    <Dropdown
                        value={value || { value: '', displayValue: '' }}
                        heading={`Select ${field.label}`}
                        label={field.label}
                        options={mapDropdownOptions(field.props.options || [])}
                        onChange={(option) => {
                            if (!editable) return;
                            onChangeValue(key, option);
                        }}
                        error={err}
                        disabled={!editable}
                    />
                </Styles.ItemWrapper>
            );
        }

        if (field.component === 'yes_no') {
            return (
                <Styles.ItemWrapper key={field.id}>
                    <Styles.GSTTitle>{field.label}</Styles.GSTTitle>
                    <Styles.CheckBoxItemWrapper>
                        <Styles.CheckBoxFlex>
                            <Checkbox
                                isSelected={!!value}
                                onSelectHandler={() => {
                                    if (!editable) return;
                                    onChangeValue(key, true);
                                }}
                                color={'#0FA457'}
                                width={'18px'}
                                height={'18px'}
                            />
                            <Styles.CheckBoxText>Yes</Styles.CheckBoxText>
                        </Styles.CheckBoxFlex>
                        <Styles.CheckBoxFlex>
                            <Checkbox
                                isSelected={!value}
                                onSelectHandler={() => {
                                    if (!editable) return;
                                    onChangeValue(key, false);
                                }}
                                color={'#0FA457'}
                                width={'18px'}
                                height={'18px'}
                            />
                            <Styles.CheckBoxText>No</Styles.CheckBoxText>
                        </Styles.CheckBoxFlex>
                    </Styles.CheckBoxItemWrapper>
                </Styles.ItemWrapper>
            );
        }

        if (field.component === 'document_upload') {
            return (
                <Styles.ItemWrapper key={field.id}>
                    <DocumentUploadWidget
                        id={field.id}
                        label={field.label}
                        title={''}
                        documentName={value?.name || ''}
                        uploadStatus={value?.uploadStatus || ''}
                        isMandatory={!!field.required}
                        disabled={!editable}
                        onSelect={(id: string, file: File) =>
                            onSelectFile(id, file, field)
                        }
                        onDelete={() => onDeleteFile(field)}
                    />
                    {err.length > 0 && (
                        <Styles.DocumentUploadErrorMessage>
                            {err}
                        </Styles.DocumentUploadErrorMessage>
                    )}
                </Styles.ItemWrapper>
            );
        }

        return null;
    };

    return (
        <>
            <Styles.Wrapper>
                <Styles.Title>{description}</Styles.Title>
                {errorMessage ? (
                    <Styles.ErrorBanner>{errorMessage}</Styles.ErrorBanner>
                ) : null}

                {sections.map(({ section, fields }) => (
                    <Styles.SectionBlock
                        key={section}
                        $animateExpand={section === 'gst' && gstExpanded}
                    >
                        {SECTION_TITLES[section] ? (
                            <Styles.SectionTitle>
                                {SECTION_TITLES[section]}
                            </Styles.SectionTitle>
                        ) : null}
                        {fields.map((field) =>
                            isFieldVisible(field, values)
                                ? renderCaptureField(field)
                                : null
                        )}
                    </Styles.SectionBlock>
                ))}
            </Styles.Wrapper>

            <Styles.BottomWrapper>
                {mode === 'capture' && tncField ? (
                    <Styles.TncWrapper>
                        <Styles.CheckBoxFlex style={{ alignItems: 'start' }}>
                            <Checkbox
                                isSelected={!!values[tncField.props.key_name]}
                                onSelectHandler={() =>
                                    onChangeValue(
                                        tncField.props.key_name,
                                        !values[tncField.props.key_name]
                                    )
                                }
                                color={'#0FA457'}
                                width={'18px'}
                                height={'18px'}
                            />
                        </Styles.CheckBoxFlex>
                        <Styles.TcnText>
                            {`By tapping on the button below, I agree to ACKO'S`}{' '}
                            <span onClick={onClickTncLink}>
                                Terms and Conditions
                            </span>
                        </Styles.TcnText>
                    </Styles.TncWrapper>
                ) : null}
                {mode === 'capture' &&
                validationErr[tncField?.props?.key_name || ''] ? (
                    <Styles.DocumentUploadErrorMessage>
                        {validationErr[tncField!.props.key_name]}
                    </Styles.DocumentUploadErrorMessage>
                ) : null}
                <Styles.ButtonWrapper $pressed={ctaPressed}>
                    <PrimaryButton
                        disabled={isBtnDisabled}
                        onClick={onSubmit}
                        loading={isBtnLoading}
                    >
                        {buttonName}
                    </PrimaryButton>
                </Styles.ButtonWrapper>
            </Styles.BottomWrapper>
        </>
    );
}

export default FinancialDetailsForm;
